// ============================================================================
// Ask AI's task card — what each action shows, and its save (item #944, the
// tasks part; TCSD-0531 one tool per part, saved through the screen's own
// function). The server half is supabase/functions/am-copilot/tasks.ts.
//
// Every save here is the function the task screen (task-detail.tsx) calls for
// the same button: addComment, resolveTask, reopenTask, updateTask (Reassign,
// Save changes), sendTaskBack. Each one now says "That didn't save" when the
// database refused the change, rather than a quiet success.
// ============================================================================

import {
  addComment,
  reopenTask,
  resolveTask,
  sendTaskBack,
  updateTask,
  type TaskEditInput,
} from '../tasks/hooks/use-task-data';
import { rpcErrorMessage } from '../../shared/rpc-error';
import { dayWords, taskButtonWords } from '../../../supabase/functions/am-copilot/buttons';
import type { FieldSpec } from './building-actions';

export const TASK_ACTIONS = ['add_comment', 'resolve', 'reopen', 'reassign', 'send_back', 'edit'] as const;
export type TaskAction = (typeof TASK_ACTIONS)[number];

/** A draft as the server persisted it (prepareTaskAction). */
export interface TaskDraft {
  task_id: string;
  action: TaskAction;
  fields: Record<string, unknown>;
  facility_id?: string | null;
  display_name?: string | null;
  context?: Record<string, unknown>;
}

export interface TaskCardSpec {
  title: string;
  fields: FieldSpec[];
  info: Array<{ label: string; value: string }>;
  readBack: string | null;
  doItLabel: string | null;
  missing: string | null;
}

const str = (v: unknown) => (typeof v === 'string' ? v : v == null ? '' : String(v));
const blank = (v: unknown) => str(v).trim().length === 0;
const ctxOf = (d: TaskDraft) => (d.context ?? {}) as Record<string, unknown>;

/** The task as people know it: what it says, and its building when it has one. */
export const taskOf = (d: TaskDraft) => {
  const c = ctxOf(d);
  const label = str(c.label) || d.task_id;
  return str(c.building) ? `${label} (${str(c.building)})` : label;
};

const PRIORITY_OPTIONS = ['High', 'Medium', 'Low'].map((p) => ({ value: p, label: p }));

/** What the card shows for this draft, with the person's edits `f`. */
export function taskCard(d: TaskDraft, f: Record<string, unknown>): TaskCardSpec {
  const c = ctxOf(d);
  const now = (c.current ?? {}) as Record<string, unknown>;
  const statusLine = { label: 'Status now', value: str(c.status_words) || '—' };
  const withLine = { label: 'With', value: str(c.assigned_to_name) || 'nobody' };
  const base: TaskCardSpec = { title: '', fields: [], info: [statusLine, withLine], readBack: null, doItLabel: taskButtonWords(d.action), missing: null };
  switch (d.action) {
    case 'add_comment':
      return {
        ...base,
        title: 'Add a comment',
        info: [withLine],
        fields: [{ key: 'body', label: 'Comment', kind: 'textarea', required: true }],
        missing: blank(f.body) ? 'Write the comment.' : null,
      };
    case 'resolve':
      return {
        ...base,
        title: 'Resolve the task',
        fields: [
          { key: 'as', label: 'Resolve as', kind: 'select', required: true, options: [{ value: 'Done', label: 'Done' }, { value: 'Deferred', label: 'Deferred' }] },
          { key: 'note', label: 'Note (saved on the task and posted as a comment)', kind: 'textarea', required: true },
        ],
        missing: blank(f.note) ? 'A note is required to resolve a task.' : f.as !== 'Done' && f.as !== 'Deferred' ? 'Pick Done or Deferred.' : null,
      };
    case 'reopen':
      return { ...base, title: 'Reopen the task', info: [statusLine] };
    case 'reassign':
      return { ...base, title: 'Reassign the task', info: [withLine, { label: 'To', value: str(c.to_name) || 'Unassigned' }] };
    case 'send_back':
      return {
        ...base,
        title: 'Send back',
        info: [withLine, { label: 'Back to', value: str(c.created_by_name) || '—' }],
        fields: [{ key: 'reason', label: 'Why it is going back', kind: 'textarea', required: true }],
        missing: blank(f.reason) ? 'Say why it is going back.' : null,
      };
    case 'edit':
      return {
        ...base,
        title: 'Change the task',
        info: [],
        fields: [
          ...('description' in f ? [{ key: 'description', label: 'What it says', kind: 'textarea' as const, now: str(now.description) || null }] : []),
          ...('priority' in f ? [{ key: 'priority', label: 'Priority', kind: 'select' as const, options: PRIORITY_OPTIONS, now: str(now.priority) || null }] : []),
          ...('due_date' in f ? [{ key: 'due_date', label: 'Due', kind: 'date' as const, now: now.due_date ? dayWords(now.due_date) : 'No due date' }] : []),
        ],
        missing: 'description' in f && blank(f.description) ? 'The task needs to say something.' : null,
      };
  }
}

export interface TaskCommitResult {
  ok: boolean;
  error?: string;
  note?: string;
  target_table?: string;
  target_row_id?: string | null;
  committed_payload?: Record<string, unknown>;
}
const fail = (error: string): TaskCommitResult => ({ ok: false, error });

/** Save one task draft through the task screen's own function. */
export async function commitTaskAction(d: TaskDraft, f: Record<string, unknown>, eid: string | null): Promise<TaskCommitResult> {
  const missing = taskCard(d, f).missing;
  if (missing) return fail(missing);
  if (!eid) return fail('Your sign-in has no employee record, so nothing can be saved in your name.');
  const id = d.task_id;
  const c = ctxOf(d);
  const ok = (note: string, committed_payload: Record<string, unknown>): TaskCommitResult =>
    ({ ok: true, note, target_table: 'facility_tasks', target_row_id: id, committed_payload });
  try {
    switch (d.action) {
      case 'add_comment':
        await addComment(id, str(f.body).trim(), eid);
        return ok('Comment added.', { comment: str(f.body).trim() });
      case 'resolve': {
        const as = f.as === 'Deferred' ? 'Deferred' : 'Done';
        await resolveTask(id, as, str(f.note).trim(), eid);
        return ok(as === 'Done' ? 'Task resolved.' : 'Task deferred.', { status: as, completion_notes: str(f.note).trim() });
      }
      case 'reopen':
        await reopenTask(id);
        return ok('Task reopened.', { status: 'Open' });
      case 'reassign': {
        const to = blank(f.to_eid) ? null : str(f.to_eid);
        await updateTask(id, { assigned_to: to });
        return ok(to ? `Reassigned to ${str(c.to_name) || to}.` : 'Task unassigned.', { assigned_to: to });
      }
      case 'send_back': {
        // The screen sends it to the person who MADE it; read that from the task, not the card.
        const createdBy = str((c as { created_by?: unknown }).created_by);
        if (!createdBy) return fail('This task has no one recorded as making it.');
        await sendTaskBack({ task_id: id, created_by: createdBy }, str(f.reason).trim(), str(c.created_by_name) || createdBy, eid);
        return ok(`Sent back to ${str(c.created_by_name) || createdBy}.`, { assigned_to: createdBy, reason: str(f.reason).trim() });
      }
      case 'edit': {
        const patch: TaskEditInput = {};
        if ('description' in f) patch.description = str(f.description).trim();
        if ('priority' in f && !blank(f.priority)) patch.priority = str(f.priority) as TaskEditInput['priority'];
        if ('due_date' in f) patch.due_date = blank(f.due_date) ? null : str(f.due_date);
        await updateTask(id, patch);
        return ok('Task changed.', patch as Record<string, unknown>);
      }
    }
  } catch (e) {
    return fail(rpcErrorMessage(e, 'That did not save.'));
  }
}
